import {
  BookingData,
  Cart,
  Order,
  CartItem,
  PaymentMethod,
  BookingEventParams,
  CartEventParams,
  PaymentEventParams,
  OrderEventParams,
  Currency
} from './tiktok.types';
import { hashEmail } from '../util/hash';

interface TikTokPixel {
  track: (eventName: string, parameters: Record<string, any>) => void;
  page: () => void;
}
type WindowWithTtq = Window & { ttq?: TikTokPixel };

const ensureTikTokLoaded = (): Promise<TikTokPixel> => {
  return new Promise((resolve) => {
    const w = window as WindowWithTtq;
    if (w.ttq) {
      resolve(w.ttq);
    } else {
      window.addEventListener('ttq.loaded', () => resolve((window as WindowWithTtq).ttq!));
    }
  });
};

export const trackTikTokEvent = async <T extends Record<string, any>>(
  eventName: string,
  parameters: T
): Promise<void> => {
  try {
    const ttq = await ensureTikTokLoaded();
    ttq.track(eventName, parameters);
  } catch (error) {
    console.error('TikTok tracking error:', error);
  }
};

export const trackSignUp = async (userData) => {
  const hashedEmail = await hashEmail(userData.email);
  return trackTikTokEvent('SignUp', {
    value: 0,
    currency: 'USD',
    content_name: 'User Signup',
    content_category: 'User Registration',
    user_id: userData.id,
    email_hash: hashedEmail,
    signup_method: userData.signupMethod || 'email',
    signup_source: userData.signupSource || 'direct',
    signup_date: new Date().toISOString()
  });
};

export const trackLogin = async (userData) => {
  const hashedEmail = await hashEmail(userData.email);
  return trackTikTokEvent('Login', {
    content_name: 'User Login',
    content_category: 'Authentication',
    user_id: userData.id,
    email_hash: hashedEmail,
    login_date: new Date().toISOString()
  });
};

export const trackBookingRequest = (bookingData: BookingData): Promise<void> => {
  const params: BookingEventParams = {
    value: bookingData.total || 0,
    currency: bookingData.currency || 'USD',
    content_name: 'Booking Request',
    content_category: 'Booking',
    booking_id: bookingData.id,
    user_id: bookingData.userId,
    booking_date: bookingData.bookingDate,
    booking_duration: bookingData.duration,
    booking_type: bookingData.type,
    booking_status: 'submitted',
    location: bookingData.location,
    service_provider: bookingData.providerId,
    service_name: bookingData.serviceName,
    number_of_guests: bookingData.guestCount,
    special_requests: bookingData.specialRequests || '',
    created_at: new Date().toISOString()
  };
  return trackTikTokEvent('SubmitBookingRequest', params);
};

const formatItems = (items: CartItem[]): CartEventParams['contents'] => {
  return items.map(item => ({
    content_id: item.id,
    content_name: item.name,
    quantity: item.quantity,
    price: item.price
  }));
};

export const trackInitiateCheckout = (cart: Cart): Promise<void> => {
  const params: CartEventParams = {
    value: cart.total,
    currency: cart.currency || 'USD',
    content_name: 'Checkout',
    content_category: 'Purchase',
    contents: formatItems(cart.items)
  };
  return trackTikTokEvent('InitiateCheckout', params);
};

export const trackAddPaymentInfo = (paymentMethod: PaymentMethod, cart: Cart): Promise<void> => {
  const params: PaymentEventParams = {
    value: cart.total,
    currency: cart.currency || 'USD',
    content_name: 'Payment Info',
    content_category: 'Purchase',
    contents: formatItems(cart.items),
    payment_method: paymentMethod
  };
  return trackTikTokEvent('AddPaymentInfo', params);
};

export const trackCompletePayment = (order: Order): Promise<void> => {
  const params: OrderEventParams = {
    value: order.total,
    currency: order.currency || 'USD',
    content_name: 'Purchase',
    content_category: 'Purchase',
    contents: formatItems(order.items),
    transaction_id: order.id,
    status: order.status
  };
  return trackTikTokEvent('CompletePayment', params);
};

export const trackAddToCart = (item: CartItem): Promise<void> => {
  const params: CartEventParams = {
    value: item.price,
    currency: 'USD',
    content_name: 'Add to Cart',
    content_category: 'Purchase',
    contents: [
      { content_id: item.id, content_name: item.name, quantity: 1, price: item.price }
    ]
  };
  return trackTikTokEvent('AddToCart', params);
};

export const trackRemoveFromCart = (item: CartItem): Promise<void> => {
  const params: CartEventParams = {
    value: item.price,
    currency: 'USD',
    content_name: 'Remove from Cart',
    content_category: 'Purchase',
    contents: [
      { content_id: item.id, content_name: item.name, quantity: 1, price: item.price }
    ]
  };
  return trackTikTokEvent('RemoveFromCart', params);
};

export const trackViewCart = (cart: Cart): Promise<void> => {
  const params: CartEventParams = {
    value: cart.total,
    currency: cart.currency || 'USD',
    content_name: 'View Cart',
    content_category: 'Purchase',
    contents: formatItems(cart.items)
  };
  return trackTikTokEvent('ViewCart', params);
};
